import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Phone, ShieldCheck, BadgeEuro } from "lucide-react";
import { COMPANY, YEARS } from "./data";
import { EASE, requestOffer } from "./scroll";
import { Seal } from "./Seal";
import { imgSrc } from "./Pic";

const LINES = ["Ein undichtes Dach", "kostet Sie jeden", "Tag Geld"];
const TRUST = [
  { v: "300+", l: "Dächer in der Region" },
  { v: String(YEARS), l: "Jahre" },
  { icon: ShieldCheck, l: "Meisterbetrieb" },
  { icon: BadgeEuro, l: "Festpreis-Garantie" },
];

const Headline = () => (
  <h1 className="font-display font-bold uppercase text-white lg:col-span-7">
    <motion.span className="eyebrow mb-5 block text-terra !tracking-[0.16em] before:hidden sm:mb-6 sm:!tracking-[0.3em] sm:before:inline-block" initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3, duration: 0.8, ease: EASE }}>
      Ihr Dachdecker in Schifferstadt
    </motion.span>
    {LINES.map((t, i) => (
      <span key={t} className="-mt-[0.12em] block overflow-hidden pt-[0.12em]">
        <motion.span
          className="block text-[min(11vw,8.6vh)] leading-[0.95] lg:text-[min(6.4vw,9.4vh)]"
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ delay: 0.4 + i * 0.12, duration: 1.1, ease: EASE }}
        >
          {t}{i === LINES.length - 1 && <span className="text-terra">.</span>}
        </motion.span>
      </span>
    ))}
    <span className="block overflow-hidden">
      <motion.span className="mt-4 block max-w-2xl text-[1.55rem] leading-[1.05] text-slate-100 sm:text-4xl lg:mt-6 lg:text-[2.5rem]" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ delay: 0.85, duration: 1.1, ease: EASE }}>
        Wir beheben es – <span className="text-terra">zum Festpreis</span>, ohne Diskussion.
      </motion.span>
    </span>
  </h1>
);

const HeroQuote = () => (
  <figure className="mt-6 border-l-2 border-terra pl-4" data-testid="hero-promise">
    <blockquote className="text-sm font-semibold leading-relaxed text-slate-100">„Was wir anbieten, halten wir – zum vereinbarten Preis und zum vereinbarten Termin.“</blockquote>
    <figcaption className="mt-1.5 text-xs text-slate-300">Unser Versprechen als Meisterbetrieb seit 2008</figcaption>
  </figure>
);

const Intro = () => (
  <motion.div className="lg:col-span-5 lg:pb-2" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0, duration: 1, ease: EASE }}>
    <p className="inline-flex items-center gap-2.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-4 py-2 text-xs font-semibold text-emerald-100 backdrop-blur sm:text-sm" data-testid="hero-availability">
      <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" /></span>
      Noch Beratungstermine diese Woche frei
    </p>
    <p className="mt-5 text-lg font-semibold leading-snug text-white sm:text-xl">Kostenlose Besichtigung innerhalb von 48 Stunden – Festpreis-Garantie.</p>
    <div className="mt-6 flex flex-col gap-3">
      <button type="button" onClick={() => requestOffer("Dachcheck & Beratung")} className="btn-primary btn-mega w-full justify-center" data-testid="hero-offer-cta">
        Jetzt kostenlosen Dachcheck sichern <ArrowRight size={20} className="btn-arrow hidden shrink-0 sm:block" />
      </button>
      <a href={COMPANY.phoneHref} className="btn-ghost btn-mega w-full justify-center bg-white/5 backdrop-blur" data-testid="hero-phone-cta">
        <Phone size={20} className="shrink-0" /> Anrufen: {COMPANY.phoneDisplay}
      </a>
    </div>
    <HeroQuote />
  </motion.div>
);

const TrustBar = () => (
  <motion.ul className="mt-8 grid grid-cols-2 gap-x-5 gap-y-4 border-t border-white/15 pt-6 sm:flex sm:flex-wrap sm:items-center sm:gap-x-10 lg:mt-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 1 }} data-testid="hero-trust-bar">
    {TRUST.map(({ v, l, icon: Icon }) => (
      <li key={l} className="flex items-center gap-2.5 text-white">
        {Icon ? <Icon size={24} className="shrink-0 text-terra" /> : <span className="font-display text-3xl font-bold leading-none text-terra">{v}</span>}
        <span className="text-xs font-bold uppercase tracking-[0.12em] text-white sm:text-sm">{l}</span>
      </li>
    ))}
  </motion.ul>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [2.5, -2.5]), { stiffness: 50, damping: 20 });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-3.5, 3.5]), { stiffness: 50, damping: 20 });

  const onMove = (e) => {
    if (e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="start" ref={ref} onPointerMove={onMove} className="relative min-h-[100svh] overflow-hidden bg-ink [perspective:1400px] lg:h-[100svh] lg:min-h-[800px]" data-testid="hero-section">
      <motion.div style={{ y: bgY, rotateX: rotX, rotateY: rotY }} className="absolute -inset-[6%] will-change-transform">
        <motion.img
          {...imgSrc("hero")}
          sizes="100vw"
          alt="Neu eingedecktes Ziegeldach vor tiefblauem Himmel – Dachdecker in Schifferstadt"
          className="h-full w-full object-cover"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1.04 }}
          transition={{ duration: 2.4, ease: EASE }}
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/10" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/75 via-ink/25 to-transparent" />
      <div className="grain absolute inset-0" />
      <Seal />
      <motion.div style={{ y: contentY, opacity: fade }} className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1440px] flex-col justify-end px-5 pb-8 pt-28 sm:px-8 lg:h-full lg:min-h-0 lg:px-12 lg:pb-10">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-10">
          <Headline />
          <Intro />
        </div>
        <TrustBar />
      </motion.div>
    </section>
  );
};
